import firebase from 'firebase/app';
import 'firebase/messaging';
import './firebase';
import {
  getNotificationWhiteLabelKey,
  registerNotificationToken,
  refreshNotificationToken,
  revokeNotificationToken,
} from '@src/services/notifications/tokens';
import { store } from '@store/store';

const VAPID_KEY = process.env.REACT_APP_FB_VAPID_KEY;
const STORAGE_KEY_DEVICE_TOKEN = 'sm_fcm_device_token';
const STORAGE_KEY_BINDING = 'sm_fcm_device_token_binding';

/**
 * Register the FCM service worker on its OWN dedicated scope so it never
 * collides with the Workbox PWA service worker (`/service-worker.js`), which
 * also claims the root scope `/`. A scope can only be controlled by ONE SW, so
 * when both register at `/` they overwrite each other. In installed-PWA mode the
 * Workbox SW (which has no `push` handler) wins the root scope, so background FCM
 * pushes were delivered to a SW that drops them — no notification showed while the
 * app was minimized. Using a separate scope gives FCM its own push subscription,
 * handled by `firebase-messaging-sw.js`, independent of the Workbox SW.
 */
const FCM_SW_SCOPE = '/firebase-cloud-messaging-push-scope';

let messagingInstance = null;
let tokenRequestInFlight = null;

async function waitForServiceWorkerActivation(registration) {
  if (!registration || registration.active) return;
  const sw = registration.installing || registration.waiting;
  if (!sw) return;
  await new Promise((resolve) => {
    const onStateChange = () => {
      if (sw.state === 'activated') {
        sw.removeEventListener('statechange', onStateChange);
        resolve();
      }
    };
    sw.addEventListener('statechange', onStateChange);
  });
}

async function registerFcmServiceWorker() {
  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
    scope: FCM_SW_SCOPE,
  });
  await waitForServiceWorkerActivation(registration);
  return registration;
}

const getMessaging = () => {
  if (!firebase.messaging.isSupported()) {
    console.warn('[FCM] firebase.messaging is not supported');
    return null;
  }
  if (!messagingInstance) messagingInstance = firebase.messaging();
  return messagingInstance;
};

export const requestFcmToken = async () => {
  if (tokenRequestInFlight) {
    console.log('[FCM] requestFcmToken: in-flight, reusing promise');
    return tokenRequestInFlight;
  }
  tokenRequestInFlight = (async () => {
    try {
      console.log('[FCM] requestFcmToken start, VAPID set?', Boolean(VAPID_KEY));
      const messaging = getMessaging();
      if (!messaging) {
        console.warn('[FCM] messaging not supported in this browser');
        return null;
      }
      const currentPermission = Notification.permission;
      if (currentPermission === 'granted') {
        console.log('[FCM] permission already granted');
      }
      const permission = currentPermission === 'granted' ? currentPermission : await Notification.requestPermission();
      console.log('[FCM] permission:', permission);
      if (permission !== 'granted') return null;
      const registration = await registerFcmServiceWorker();
      console.log('[FCM] SW ready:', registration?.scope);
      const token = await messaging.getToken({
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: registration,
      });
      console.log('[FCM] token:', token);
      return token || null;
    } catch (err) {
      console.error('[FCM] getToken error:', err);
      return null;
    } finally {
      tokenRequestInFlight = null;
    }
  })();
  return tokenRequestInFlight;
};

export const requestFcmTokenWithPermission = async () => {
  if (typeof Notification === 'undefined') return { token: null, permission: null };
  const currentPermission = Notification.permission;
  const permission = currentPermission === 'granted' ? currentPermission : await Notification.requestPermission();
  if (permission !== 'granted') return { token: null, permission };

  const token = await requestFcmToken();
  return { token, permission };
};

function getCurrentBinding() {
  try {
    const state = store.getState();
    const userId = state?.Auth?.profile?.id || null;
    const whiteLabelKey = getNotificationWhiteLabelKey();
    return { userId, whiteLabelKey };
  } catch {
    return { userId: null, whiteLabelKey: null };
  }
}

function getStoredDeviceToken() {
  try {
    return localStorage.getItem(STORAGE_KEY_DEVICE_TOKEN);
  } catch {
    return null;
  }
}

function getStoredBinding() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_BINDING);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      userId: parsed.userId ?? null,
      whiteLabelKey: parsed.whiteLabelKey ?? null,
    };
  } catch {
    return null;
  }
}

function setStoredDeviceToken(token) {
  try {
    if (!token) return;
    localStorage.setItem(STORAGE_KEY_DEVICE_TOKEN, token);
  } catch {
    // ignore
  }
}

function setStoredBinding(binding) {
  try {
    localStorage.setItem(STORAGE_KEY_BINDING, JSON.stringify(binding || {}));
  } catch {
    // ignore
  }
}

function clearStoredDeviceToken() {
  try {
    localStorage.removeItem(STORAGE_KEY_DEVICE_TOKEN);
  } catch {
    // ignore
  }
}

function clearStoredBinding() {
  try {
    localStorage.removeItem(STORAGE_KEY_BINDING);
  } catch {
    // ignore
  }
}

async function syncTokenWithBackend(newToken) {
  const currentBinding = getCurrentBinding();
  const oldToken = getStoredDeviceToken();
  const oldBinding = getStoredBinding();

  // User/brand switched in the same browser: revoke old token before registering for new session.
  if (
    oldToken &&
    oldBinding &&
    (oldBinding.userId !== currentBinding.userId || oldBinding.whiteLabelKey !== currentBinding.whiteLabelKey)
  ) {
    console.log('[FCM] binding changed: revoke old token before re-register');
    try {
      await revokeNotificationToken(oldToken);
    } catch {
      // ignore
    } finally {
      clearStoredDeviceToken();
      clearStoredBinding();
    }
  }

  const tokenAfterRevoke = getStoredDeviceToken();

  if (!tokenAfterRevoke) {
    try {
      await registerNotificationToken(newToken);
    } catch (err) {
      // Backend may reject duplicate token (e.g. 409 Conflict) when localStorage
      // was cleared but the same FCM token is still valid in IndexedDB.
      // Store it locally regardless so we don't keep retrying a failing
      // registration on every mount.
      console.warn('[FCM] registerNotificationToken failed (storing locally anyway):', err?.message || err);
    }
    setStoredDeviceToken(newToken);
    setStoredBinding(currentBinding);
    return;
  }

  if (tokenAfterRevoke === newToken) {
    // Ensure binding is up-to-date even if token unchanged.
    setStoredBinding(currentBinding);
    return;
  }

  try {
    await refreshNotificationToken(tokenAfterRevoke, newToken);
  } catch (e) {
    // Fallback: if refresh endpoint rejects old token, try plain register.
    try {
      await registerNotificationToken(newToken);
    } catch (regErr) {
      console.warn('[FCM] fallback registerNotificationToken also failed:', regErr?.message || regErr);
    }
  }

  setStoredDeviceToken(newToken);
  setStoredBinding(currentBinding);
}

export const requestAndSyncFcmToken = async () => {
  const token = await requestFcmToken();
  if (!token) return null;
  await syncTokenWithBackend(token);
  return token;
};

export const revokeStoredFcmToken = async () => {
  const token = getStoredDeviceToken();
  if (!token) return;
  try {
    await revokeNotificationToken(token);
  } finally {
    clearStoredDeviceToken();
    clearStoredBinding();
  }
};

export const markFcmTokenInvalid = async ({ autoReregister = true } = {}) => {
  console.warn('[FCM] token marked invalid: clearing local token');
  clearStoredDeviceToken();
  clearStoredBinding();

  if (!autoReregister) return;
  const hasAuth = Boolean(localStorage.getItem('userData'));
  if (!hasAuth) return;
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;

  try {
    await requestAndSyncFcmToken();
  } catch {
    // ignore
  }
};

export const onForegroundMessage = (handler) => {
  const messaging = getMessaging();
  if (!messaging) return () => {};
  console.log('[FCM] foreground listener registered');
  return messaging.onMessage(handler);
};
